import DOMPurify from "dompurify";

export const ABOUT_HTML_MARKER = "<!--easyplanner-about:v1-->";
export const ABOUT_MAX_LENGTH = 100000;
export const ABOUT_WIDGET_HTML = '<div data-booking-widget="true"></div>';
/** Card icons by name: `ep-icon-<name>` in the HTML, a bootstrap icon on the page. */
export const ABOUT_ICONS = {
  calendar: "bi-calendar-check", people: "bi-people", chart: "bi-bar-chart-line", clock: "bi-clock",
  star: "bi-stars", heart: "bi-heart", shield: "bi-shield-check", chat: "bi-chat-dots",
  award: "bi-award", gem: "bi-gem", scissors: "bi-scissors", flower: "bi-flower2",
  pin: "bi-geo-alt", lightning: "bi-lightning-charge", check: "bi-check2-circle", gift: "bi-gift",
};
// The blocks the About editor writes (utils/aboutBlocks.js), and the three older
// layout classes. Mirrored by AboutContentSanitizer.cs, which applies the same
// list on the server: a class added here and not there is stripped on save.
export const ABOUT_CLASSES = [
  "ep-about-grid", "ep-about-card", "ep-about-lead",
  "ep-block", "ep-block-text", "ep-block-cards", "ep-block-media", "ep-block-faq", "ep-block-link", "ep-block-booking",
  "ep-align-center", "ep-cards", "ep-card", "ep-media-left", "ep-media-right", "ep-media-body", "ep-eyebrow",
  "ep-button", "ep-button-outline",
  ...Object.keys(ABOUT_ICONS).map((name) => `ep-icon-${name}`),
];
const classes = new Set(ABOUT_CLASSES);
const tags = ["p", "br", "h2", "h3", "strong", "b", "em", "i", "u", "s", "ul", "ol", "li", "blockquote", "hr", "a", "img", "div", "figure", "figcaption", "details", "summary"];
export const escapeAboutText = (value) => String(value || "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);
const storageOrigin = () => new URL(import.meta.env.VITE_API_BASE_URL || window.location.origin, window.location.origin).origin;

export const safeAboutUrl = (value, image = false) => {
  if (!value || /[\u0000-\u0020\\]/.test(value) || value.startsWith("//")) return null;
  if (image && /^\/uploads\/[a-zA-Z0-9_./%-]+$/.test(value) && !value.includes("..")) return value;
  if (!image && /^(?:#|\/(?!\/))/.test(value)) return value;
  if (!image && !value.includes(":")) return value;
  try {
    const parsed = new URL(value);
    if (["https:", "http:"].includes(parsed.protocol) && !parsed.username && !parsed.password) {
      if (image) {
        if (parsed.origin !== storageOrigin() || !parsed.pathname.startsWith("/uploads/") || parsed.search || parsed.hash) return null;
      }
      return parsed.href;
    }
    if (!image && ["mailto:", "tel:"].includes(parsed.protocol)) return value;
  } catch { /* Invalid URLs are removed. */ }
  return null;
};

/**
 * The src to store for an image the API has just saved. The API names every file under one
 * configured origin (FileStorage:PublicBaseUrl, the apex), but production is served on www too,
 * and safeAboutUrl admits only the page's own origin. When the API shares the page's origin the
 * path alone resolves on every host, so the saved HTML carries none of them.
 */
export const aboutUploadSrc = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    if (storageOrigin() === window.location.origin && parsed.pathname.startsWith("/uploads/") && !parsed.search && !parsed.hash) return parsed.pathname;
  } catch { /* safeAboutUrl rejects what does not parse. */ }
  return url;
};

/** Inert parsing only: our own embed loader is converted to a native booking block. */
export const normalizeAboutEmbeds = (html, origin = window.location.origin) => {
  const document = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  for (const script of document.querySelectorAll("script")) {
    try {
      const src = new URL(script.getAttribute("src"), origin);
      if (src.origin === origin && src.pathname === "/embed.js" && script.getAttribute("data-slug")) {
        const widget = document.createElement("div");
        widget.setAttribute("data-booking-widget", "true");
        script.replaceWith(widget);
      }
    } catch { /* Other scripts are stripped by DOMPurify. */ }
  }
  return document.body.innerHTML;
};

export const sanitizeAboutHtml = (html) => {
  const clean = DOMPurify.sanitize(String(html || ""), {
    ALLOWED_TAGS: tags,
    ALLOWED_ATTR: ["href", "src", "alt", "title", "class", "data-booking-widget"],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
  const document = new DOMParser().parseFromString(clean, "text/html");
  for (const element of document.body.querySelectorAll("*")) {
    const tag = element.tagName.toLowerCase();
    for (const attribute of [...element.attributes]) {
      const name = attribute.name;
      const allowed = (name === "class") || (tag === "a" && ["href", "title"].includes(name)) ||
        (tag === "img" && ["src", "alt", "title"].includes(name)) ||
        (tag === "div" && name === "data-booking-widget" && attribute.value === "true");
      if (!allowed) element.removeAttribute(name);
    }
    if (element.hasAttribute("class")) {
      const kept = element.className.split(/\s+/).filter((name) => classes.has(name)).join(" ");
      if (kept) element.className = kept; else element.removeAttribute("class");
    }
    if (tag === "a") {
      const href = safeAboutUrl(element.getAttribute("href"));
      if (href) element.setAttribute("href", href); else element.removeAttribute("href");
    }
    if (tag === "img") {
      const src = safeAboutUrl(element.getAttribute("src"), true);
      if (src) element.setAttribute("src", src); else element.remove();
    }
    if (tag === "div" && element.getAttribute("data-booking-widget") === "true") element.replaceChildren();
  }
  return document.body.innerHTML;
};

export const aboutToHtml = (value) => value?.startsWith(ABOUT_HTML_MARKER)
  ? sanitizeAboutHtml(value.slice(ABOUT_HTML_MARKER.length))
  : String(value || "").split(/\r?\n\s*\r?\n/).map((text) => `<p>${escapeAboutText(text).replace(/\r?\n/g, "<br>")}</p>`).join("");

export const hasAboutContent = (value) => {
  const document = new DOMParser().parseFromString(aboutToHtml(value), "text/html");
  return Boolean(document.body.textContent.trim() || document.querySelector("img, [data-booking-widget]"));
};

export const serializeAboutHtml = (html) => {
  const clean = sanitizeAboutHtml(html);
  return hasAboutContent(ABOUT_HTML_MARKER + clean) ? ABOUT_HTML_MARKER + clean : "";
};
